import { supabase } from '../supabaseClient';

export const programmeService = {
  async getProgrammeData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        return null;
      }

      // Get programme data
      const { data: programmeData, error } = await supabase
        .from('programme_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        return null;
      }

      if (!programmeData) {
        return null;
      }

      return {
        pdfUrl: programmeData.pdf_url || '',
        pdfPath: programmeData.pdf_path || '',
        buttonText: programmeData.button_text || 'Voir le Programme',
        isVisible: programmeData.is_visible !== false,
        flashInfoPdfUrl: programmeData.flash_info_pdf_url || '',
        flashInfoPdfPath: programmeData.flash_info_pdf_path || '',
        flashInfoButtonText: programmeData.flash_info_button_text || 'Flash Info',
        flashInfoIsVisible: programmeData.flash_info_is_visible !== false,
        // Add new PDF buttons
        pdf2Url: programmeData.pdf2_url || '',
        pdf2Path: programmeData.pdf2_path || '',
        pdf2ButtonText: programmeData.pdf2_button_text || 'Document 2',
        pdf2IsVisible: programmeData.pdf2_is_visible !== false,
        pdf3Url: programmeData.pdf3_url || '',
        pdf3Path: programmeData.pdf3_path || '',
        pdf3ButtonText: programmeData.pdf3_button_text || 'Document 3',
        pdf3IsVisible: programmeData.pdf3_is_visible !== false
      };
    } catch (error) {
      return null;
    }
  },

  async updateProgrammeData(programmeData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        pdf_path: programmeData.pdfPath || '',
        pdf_url: programmeData.pdfUrl || '',
        button_text: programmeData.button_text || 'Voir le Programme',
        is_visible: programmeData.isVisible !== false,
        // Add missing flash_info fields
        flash_info_pdf_path: programmeData.flashInfoPdfPath || '',
        flash_info_pdf_url: programmeData.flashInfoPdfUrl || '',
        flash_info_button_text: programmeData.flashInfoButtonText || 'Flash Info',
        flash_info_is_visible: programmeData.flashInfoIsVisible !== false,
        // Add new PDF buttons
        pdf2_path: programmeData.pdf2Path || '',
        pdf2_url: programmeData.pdf2Url || '',
        pdf2_button_text: programmeData.pdf2ButtonText || 'Document 2',
        pdf2_is_visible: programmeData.pdf2IsVisible !== false,
        pdf3_path: programmeData.pdf3Path || '',
        pdf3_url: programmeData.pdf3Url || '',
        pdf3_button_text: programmeData.pdf3ButtonText || 'Document 3',
        pdf3_is_visible: programmeData.pdf3IsVisible !== false,
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('programme_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('programme_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('programme_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      throw error;
    }
  },

  async uploadProgrammePDF(file, editionYear) {
    try {
      const filename = `programme-${Date.now()}-${file.name}`;
      const filePath = `${editionYear}/programme/${filename}`;
      
      const { data, error } = await supabase
        .storage
        .from('speaker-images')
        .upload(filePath, file, { upsert: true });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase
        .storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadFlashInfoPDF(file, editionYear) {
    try {
      const filename = `flashinfo-${Date.now()}-${file.name}`;
      const filePath = `${editionYear}/flashinfo/${filename}`;
      
      const { data, error } = await supabase
        .storage
        .from('speaker-images')
        .upload(filePath, file, { upsert: true });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase
        .storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async deleteProgrammePDF(filePath) {
    try {
      const { error } = await supabase
        .storage
        .from('speaker-images')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async deleteFlashInfoPDF(filePath) {
    try {
      const { error } = await supabase
        .storage
        .from('speaker-images')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async uploadPDF2(file, editionYear) {
    try {
      const filename = `pdf2-${Date.now()}-${file.name}`;
      const filePath = `${editionYear}/pdf2/${filename}`;
      
      const { data, error } = await supabase
        .storage
        .from('speaker-images')
        .upload(filePath, file, { upsert: true });

      if (error) throw error;

      const { data: publicUrlData } = supabase
        .storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadPDF3(file, editionYear) {
    try {
      const filename = `pdf3-${Date.now()}-${file.name}`;
      const filePath = `${editionYear}/pdf3/${filename}`;
      
      const { data, error } = await supabase
        .storage
        .from('speaker-images')
        .upload(filePath, file, { upsert: true });

      if (error) throw error;

      const { data: publicUrlData } = supabase
        .storage
        .from('speaker-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrlData.publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async deletePDF2(filePath) {
    try {
      const { error } = await supabase
        .storage
        .from('speaker-images')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async deletePDF3(filePath) {
    try {
      const { error } = await supabase
        .storage
        .from('speaker-images')
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
